/**
 * @file Recalls entry row, shared draft rows, and warning workflows.
 */

import { appData, state } from "../../app/state/state.js";
import {
    AUTO_GROWING_TEXTAREA_CLASSES,
    bindNativeFieldBehavior,
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    escapeHTML,
    formatContentForSave,
} from "../../shared/utils/contentUtils.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    createRecallEntry,
    deleteRecallDraft,
    promoteRecallDraft,
    saveRecallDraft,
} from "./recallsApi.js";
import {
    hasRecallReasons,
    getRecallReasonDisplay,
    normalizeRecallReasonIds,
    serializeRecallReasonIds,
} from "./recallsReasonUtils.js";
import {
    bindRecallPhoneInputSanitizer,
    isValidRecallPhoneNumber,
} from "./recallsPhoneValidation.js";
import {
    createRecallProviderSelectPreset,
    getCurrentUserRecallProviderDefault,
    getRecallProviderDisplay,
    getRecallProviderControlValues,
    getRecallProviderPayload,
    parseRecallProviderMirrorValue,
} from "./recallsProviderUtils.js";
import {
    getRecallsCallableErrorType,
    openRecallsConfigIncompleteModal,
} from "./recallsConfigIncomplete.js";
import {
    refreshRecallsAfterPermanentSave,
    refreshRecallsAfterDraftPromotion,
} from "./recallsPromotionUi.js";

const FIELD_INPUT_CLASS =
    `${NATIVE_INPUT_CLASSES} !min-w-0 !min-h-[30px] !py-1 !text-sm`;
const NOTES_INPUT_CLASS =
    `${AUTO_GROWING_TEXTAREA_CLASSES} block min-w-[12rem] !min-h-[30px] !py-1 !text-sm`;
const ENTRY_CELL_CLASS = "px-2 py-2 align-middle";
const ENTRY_ACTION_CELL_CLASS = "px-1.5 py-2 align-middle";
const ENTRY_ACTION_ROW_CLASS = "flex min-h-[30px] items-center justify-start gap-1";
const DRAFT_TABLE_COLUMN_WIDTHS = Object.freeze([
    "8rem",
    "8rem",
    "14rem",
    "9rem",
    "12rem",
    "13rem",
    "10rem",
    "16rem",
    "4rem",
]);
const DRAFT_SAVE_ERRORS_WITH_MODAL = new WeakSet();
const DRAFT_SAVE_SESSIONS = new Map();

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function getReasonOptions() {
    return (appData.config?.recalls?.recallReasons || [])
        .filter((option) => option?.id && option?.isActive !== false);
}

function getFieldValue(data, key) {
    return data?.[key] || "";
}

function getReasonControlOptions() {
    return getReasonOptions().map((option) => ({
        value: option.id,
        label: option.name || option.id,
        source: option,
    }));
}

function renderReasonControl(data = {}) {
    const value = serializeRecallReasonIds(getFieldValue(data, "recall_reason"));
    return `
        <div data-recalls-entry-reason-control></div>
        <input type="hidden" name="recall_reason" value="${attrValue(value)}" data-recalls-entry-reason-mirror data-recalls-entry-input>
    `;
}

function renderProviderControl(data = {}) {
    const providers = Array.isArray(data.provider) && data.provider.length > 0 ?
        data.provider :
        getCurrentUserRecallProviderDefault(appData, state);
    const selectedIds = getRecallProviderControlValues(providers);
    return `
        <div data-recalls-entry-provider-control></div>
        <input type="hidden" name="provider" value="${attrValue(JSON.stringify(selectedIds))}" data-recalls-entry-provider-mirror data-recalls-entry-input>
    `;
}

function renderDateControl(data = {}) {
    const value = getFieldValue(data, "recall_date");
    return `
        <div data-recalls-entry-date-control></div>
        <input type="hidden" name="recall_date" value="${attrValue(value)}" data-recalls-entry-date-mirror data-recalls-entry-input>
    `;
}

/**
 * Renders editable Recalls entry cells.
 * @param {object} [data={}] Existing entry/draft data.
 * @param {object} [options={}] Render options.
 * @param {boolean} [options.includeNotes=true] Whether to include notes cell.
 * @returns {string} Entry cell HTML.
 */
export function renderEntryInputs(data = {}, { includeNotes = true } = {}) {
    return `
        <td class="${ENTRY_CELL_CLASS}">
            <input name="first_name" value="${attrValue(getFieldValue(data, "first_name"))}" placeholder="First" class="${FIELD_INPUT_CLASS}" data-recalls-entry-input>
        </td>
        <td class="${ENTRY_CELL_CLASS}">
            <input name="last_name" value="${attrValue(getFieldValue(data, "last_name"))}" placeholder="Last" class="${FIELD_INPUT_CLASS}" data-recalls-entry-input>
        </td>
        <td class="${ENTRY_CELL_CLASS}">
            <input name="email" value="${attrValue(getFieldValue(data, "email"))}" placeholder="Email" class="${FIELD_INPUT_CLASS}" data-recalls-entry-input>
        </td>
        <td class="${ENTRY_CELL_CLASS}">
            <input type="tel" name="phone" value="${attrValue(getFieldValue(data, "phone"))}" placeholder="Phone" class="${FIELD_INPUT_CLASS}" data-recalls-entry-input>
        </td>
        <td class="${ENTRY_CELL_CLASS}">${renderReasonControl(data)}</td>
        <td class="${ENTRY_CELL_CLASS}">${renderProviderControl(data)}</td>
        <td class="${ENTRY_CELL_CLASS}">${renderDateControl(data)}</td>
        ${includeNotes ? `
            <td class="${ENTRY_CELL_CLASS}">
                <textarea name="notes" rows="1" placeholder="Notes" class="${NOTES_INPUT_CLASS}" data-auto-grow="true" data-recalls-entry-input>${escapeHTML(getFieldValue(data, "notes"))}</textarea>
            </td>
        ` : ""}
    `;
}

function getProviderPayload(control) {
    if (control instanceof HTMLSelectElement) {
        return Array.from(control.selectedOptions || [])
            .map((option) => ({
                type: "app",
                userId: option.value,
            }));
    }

    try {
        const parsed = JSON.parse(control?.value || "[]");
        return getRecallProviderPayload(parsed, appData.config);
    } catch {
        return [];
    }
}

/**
 * Collects Recall entry data from an entry row/form root.
 * @param {HTMLElement} root Entry root.
 * @returns {object} Draft/create payload.
 */
export function collectRecallEntryData(root) {
    const getInput = (name) => root.querySelector(`[name="${name}"]`);
    return {
        first_name: getInput("first_name")?.value.trim() || null,
        last_name: getInput("last_name")?.value.trim() || null,
        email: getInput("email")?.value.trim() || null,
        phone: getInput("phone")?.value.trim() || null,
        recall_reason: normalizeRecallReasonIds(getInput("recall_reason")?.value),
        provider: getProviderPayload(getInput("provider")),
        recall_date: getInput("recall_date")?.value || null,
        notes: formatContentForSave(getInput("notes")?.value.trim() || "") || null,
    };
}

function hasRequiredPermanentFields(data) {
    return Boolean(
        data.first_name &&
        data.last_name &&
        hasRecallReasons(data.recall_reason) &&
        data.recall_date &&
        Array.isArray(data.provider) &&
        data.provider.length > 0
    );
}

function hasAnyContactInput(data) {
    return Boolean(data?.email || data?.phone);
}

function hasAnyEntryData(data) {
    return Boolean(
        data.first_name ||
        data.last_name ||
        data.email ||
        data.phone ||
        hasRecallReasons(data.recall_reason) ||
        data.recall_date ||
        data.notes ||
        (Array.isArray(data.provider) && data.provider.length > 0)
    );
}

/**
 * Returns whether a Recall draft has enough content to be saved.
 * @param {object} data Entry payload.
 * @returns {boolean} True when any draft field has content.
 */
export function isRecallDraftSaveReady(data) {
    return hasAnyEntryData(data);
}

function getNormalizedComparableValue(value) {
    if (Array.isArray(value)) {
        if (value.every((entry) => typeof entry !== "object")) {
            return JSON.stringify(normalizeRecallReasonIds(value));
        }
        return JSON.stringify(value.map((entry) => ({
            legacyKey: entry?.legacyKey || null,
            type: entry?.type || null,
            userId: entry?.userId || null,
        })));
    }
    return String(value || "");
}

function hasDraftDataChanges(source, data) {
    return [
        "email",
        "first_name",
        "last_name",
        "notes",
        "phone",
        "provider",
        "recall_date",
        "recall_reason",
    ].some((key) =>
        getNormalizedComparableValue(source?.[key]) !==
            getNormalizedComparableValue(data?.[key]));
}

function copyDraftEntryData(data = {}) {
    return {
        email: data.email || null,
        first_name: data.first_name || null,
        last_name: data.last_name || null,
        notes: data.notes || null,
        phone: data.phone || null,
        provider: Array.isArray(data.provider) ?
            data.provider.map((entry) => ({ ...entry })) :
            [],
        recall_date: data.recall_date || null,
        recall_reason: normalizeRecallReasonIds(data.recall_reason),
    };
}

function getDraftSaveSession(draft) {
    const draftId = draft?.id;
    if (!draftId) {return null;}
    let session = DRAFT_SAVE_SESSIONS.get(draftId);
    if (!session) {
        session = {
            baselineData: copyDraftEntryData(draft),
            expectedUpdatedAt: draft.updatedAt || null,
            inFlight: null,
            pending: false,
        };
        DRAFT_SAVE_SESSIONS.set(draftId, session);
    }
    return session;
}

function getLatestDraftExpectedUpdatedAt(draft) {
    return getDraftSaveSession(draft)?.expectedUpdatedAt || draft?.updatedAt || null;
}

/**
 * Returns whether quick-add should create a permanent entry.
 * @param {object} data Entry payload.
 * @returns {boolean} True when all required and valid contact fields are present.
 */
export function isQuickAddPermanentReady(data) {
    return hasRequiredPermanentFields(data) &&
        Boolean(data.email && data.phone) &&
        isValidRecallPhoneNumber(data.phone);
}

/**
 * Returns whether quick-add can attempt a permanent save from the UI.
 * @param {object} data Entry payload.
 * @returns {boolean} True when the permanent save action can be attempted.
 */
export function isQuickAddSaveReady(data) {
    return hasRequiredPermanentFields(data) && hasAnyContactInput(data);
}

function isNearDate(data) {
    const threshold = Number(appData.config?.recalls?.nearDateWarningDays);
    if (!Number.isFinite(threshold) || threshold < 0 || !data?.recall_date) {
        return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const recallDate = new Date(`${data.recall_date}T00:00:00`);
    if (Number.isNaN(recallDate.getTime())) {return false;}
    const diffDays = Math.round((recallDate - today) / 86400000);
    return diffDays >= 0 && diffDays <= threshold;
}

function showErrorModal(title, message) {
    openConfirmationModal(
        title,
        escapeHTML(message),
        async () => {},
        false,
        "OK",
    );
}

function runAfterConfirmation({
    body,
    buttonText,
    onCancel = null,
    onConfirm,
    options = {},
    title,
}) {
    openConfirmationModal(
        title,
        body,
        onConfirm,
        false,
        buttonText,
        onCancel,
        "Saving...",
        "Cancel",
        { maxWidth: "sm", scrollBody: false, ...options },
    );
}

function renderWarningBody(paragraphs = []) {
    return `
        <div class="space-y-2 text-gray-600">
            ${paragraphs
                .filter(Boolean)
                .map((paragraph) => `<p>${escapeHTML(paragraph)}</p>`)
                .join("")}
        </div>
    `;
}

function getMissingContactSentence(fields = []) {
    const missingFields = new Set(fields);
    const emailMissing = missingFields.has("email");
    const emailInvalid = missingFields.has("email_invalid");
    const phoneMissing = missingFields.has("phone");
    const phoneInvalid = missingFields.has("phone_invalid");
    if ((emailMissing || emailInvalid) && (phoneMissing || phoneInvalid)) {
        if (emailInvalid && phoneInvalid) {
            return "Email and phone number do not match the supported formats.";
        }
        if (emailInvalid && phoneMissing) {
            return "Email does not match the supported format and phone number is missing.";
        }
        if (emailMissing && phoneInvalid) {
            return "Email is missing and the phone number does not match the supported format.";
        }
        return "Email and phone number are missing.";
    }
    if (emailInvalid) {
        return "Email does not match the supported format.";
    }
    if (emailMissing) {
        return "Email is missing.";
    }
    if (phoneInvalid) {
        return "Phone number does not match the supported format.";
    }
    if (phoneMissing) {
        return "Phone number is missing.";
    }
    return "Contact details are missing.";
}

function getInvalidContactFields(fields = []) {
    const missingFields = new Set(fields);
    return [
        missingFields.has("email_invalid") ? "email" : null,
        missingFields.has("phone_invalid") ? "phone number" : null,
    ].filter(Boolean);
}

function getInvalidContactClearSentence(fields = []) {
    const invalidFields = getInvalidContactFields(fields);
    if (invalidFields.length === 0) {return "";}
    const fieldLabel = invalidFields.length === 1 ?
        invalidFields[0] :
        `${invalidFields.slice(0, -1).join(", ")} and ${invalidFields.at(-1)}`;
    const verb = invalidFields.length === 1 ? "will be" : "will be";
    return `If you save anyway, the invalid ${fieldLabel} ${verb} cleared before saving.`;
}

function getRecallStatusLabel(statusId) {
    const status = (appData.config?.recallStatus || [])
        .find((option) => option?.id === statusId);
    return status?.name || statusId || "No status";
}

function getRecallMonthLabel(dateString) {
    if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateString))) {
        return "that month";
    }
    const [, year, month] = String(dateString)
        .match(/^(\d{4})-(\d{2})-\d{2}$/) || [];
    if (!month || !year) {return "that month";}
    return new Intl.DateTimeFormat("en-AU", {
        month: "long",
        timeZone: "UTC",
        year: "numeric",
    }).format(new Date(`${dateString}T00:00:00.000Z`));
}

function formatStoredDateForWarning(dateString) {
    if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateString))) {
        return "";
    }
    const [, year, month, day] = String(dateString)
        .match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
    return year && month && day ? `${day}/${month}/${year}` : "";
}

function formatTimestampDateForWarning(value) {
    if (!value) {return "";}
    const date = value?.toDate ? value.toDate() :
        typeof value.seconds === "number" ?
            new Date(value.seconds * 1000) :
            typeof value._seconds === "number" ?
                new Date(value._seconds * 1000) :
                new Date(value);
    if (Number.isNaN(date.getTime())) {return "";}
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
}

function getDuplicateRecallName(match = {}) {
    return [match.first_name, match.last_name]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" ") ||
        match.email ||
        "Unnamed recall";
}

function getDuplicateMatchRows(response = {}) {
    return [
        ...(response.duplicates?.active || []),
        ...(response.duplicates?.recentlyClosed || []),
    ];
}

function getMatchedByLabel(matchFields = []) {
    const labels = (Array.isArray(matchFields) ? matchFields : [])
        .map((field) => {
            if (field === "email") {return "Email";}
            if (field === "phone") {return "Phone";}
            return field;
        })
        .filter(Boolean);
    if (labels.length === 0) {return "Contact details";}
    if (labels.length === 1) {return labels[0];}
    return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

function renderDuplicateMatchCard(match = {}, options = {}) {
    const recallDate = formatStoredDateForWarning(match.recall_date) ||
        match.recall_date ||
        "No recall date";
    const status = getRecallStatusLabel(match.status);
    const reason = getRecallReasonDisplay(
        match.recall_reason,
        appData.config?.recalls?.recallReasons || [],
    ) || "No reason";
    const provider = getRecallProviderDisplay(match.provider) || "No provider";
    const closedDate = formatTimestampDateForWarning(match.closedAt);
    const newRecallDate = formatStoredDateForWarning(options.newRecallDate);
    const dateRows = options.recentlyClosed ? [
        ["New recall date", newRecallDate || "No recall date"],
        ["Recently closed recall", recallDate],
        ["Closed date", closedDate || "No closed date"],
    ] : [
        ["Recall date", recallDate],
    ];
    const rows = [
        ["Matched by", getMatchedByLabel(match.matchFields)],
        ...dateRows,
        ["Status", status],
        ["Reason", reason],
        ["Provider", provider],
    ];

    return `
        <div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <div class="font-semibold text-gray-800">${escapeHTML(getDuplicateRecallName(match))}</div>
            <dl class="mt-2 space-y-1 text-xs text-gray-500">
                ${rows.map(([label, value]) => `
                    <div>
                        <dt class="inline font-medium text-gray-600">${escapeHTML(label)}:</dt>
                        <dd class="inline">${escapeHTML(value)}</dd>
                    </div>
                `).join("")}
            </dl>
        </div>
    `;
}

function renderDuplicateWarningBody(response = {}) {
    const matches = getDuplicateMatchRows(response);
    const hasBlockingDuplicates = response.blocked === true;
    const activeMatches = response.duplicates?.active || [];
    const recentlyClosedMatches = response.duplicates?.recentlyClosed || [];
    if (matches.length === 0) {
        return renderWarningBody([
            response.message ||
                "A recall with matching contact details already exists.",
            hasBlockingDuplicates ? "" : "Do you want to save anyway?",
        ]);
    }

    if (hasBlockingDuplicates) {
        const monthLabel = getRecallMonthLabel(response.recallDate);
        const blockingMatches = response.duplicates?.blockingActive?.length ?
            response.duplicates.blockingActive :
            activeMatches;
        return `
            <div class="space-y-3 text-gray-600">
                <p>A recall with matching contact details already exists in the same recall month.</p>
                <p>This recall cannot be saved because the patient would have more than one active recall in ${escapeHTML(monthLabel)}.</p>
                <div class="space-y-2">
                    ${blockingMatches.map((match) =>
                        renderDuplicateMatchCard(match)).join("")}
                </div>
            </div>
        `;
    }

    if (activeMatches.length === 0 && recentlyClosedMatches.length > 0) {
        return `
            <div class="space-y-3 text-gray-600">
                <p>This patient has a recently closed recall. Check that the new recall date is intentional before saving.</p>
                <div class="space-y-2">
                    ${recentlyClosedMatches.map((match) =>
                        renderDuplicateMatchCard(match, {
                            recentlyClosed: true,
                            newRecallDate: response.recallDate,
                        })).join("")}
                </div>
                <p>Do you want to save anyway?</p>
            </div>
        `;
    }

    return `
        <div class="space-y-3 text-gray-600">
            <p>A recall with matching contact details already exists in a different recall month.</p>
            <p>Review the existing recall before saving. You can continue if this is a separate recall.</p>
            <div class="space-y-2">
                ${activeMatches.map((match) =>
                    renderDuplicateMatchCard(match)).join("")}
                ${recentlyClosedMatches.map((match) =>
                    renderDuplicateMatchCard(match, {
                        recentlyClosed: true,
                        newRecallDate: response.recallDate,
                    })).join("")}
            </div>
            <p>Do you want to save anyway?</p>
        </div>
    `;
}

function getNearDateWarningBody() {
    const threshold = Number(appData.config?.recalls?.nearDateWarningDays);
    const dayLabel = threshold === 1 ? "day" : "days";
    return renderWarningBody([
        `This recall is within the next ${threshold} ${dayLabel}. Check that the recall date is intentional before saving.`,
    ]);
}

/**
 * Runs a Recall save action after optional near-date confirmation.
 * @param {object} data Recall entry data.
 * @param {Function} action Save action.
 * @returns {*}
 */
export function confirmNearDateIfNeeded(data, action) {
    if (!isNearDate(data)) {
        return Promise.resolve(action());
    }

    return new Promise((resolve, reject) => {
        runAfterConfirmation({
            title: "Near Recall Date",
            body: getNearDateWarningBody(),
            buttonText: "Save anyway",
            onCancel: () => resolve(null),
            onConfirm: () => {
                setTimeout(() => {
                    Promise.resolve(action())
                        .then(resolve)
                        .catch(reject);
                }, 0);
                return true;
            },
        });
    });
}

function getCallableErrorMessage(error) {
    return error?.message || "The Recalls action could not be completed.";
}

function getCallableErrorType(error) {
    return getRecallsCallableErrorType(error);
}

function continueWarningResponseAfterConfirmation(params) {
    setTimeout(() => {
        handleRecallWarningResponse(params).catch((error) => {
            console.error("Error handling Recalls warning response:", error);
            showErrorModal(
                "Recalls Warning",
                error?.message || "The recall was not saved.",
            );
        });
    }, 0);
}

function mergeDuplicateOverrideContext(currentContext, nextContext) {
    if (!nextContext?.scope) {return nextContext || currentContext || null;}
    const merged = currentContext && !currentContext.scope ?
        { ...currentContext } :
        currentContext?.scope ?
            { [currentContext.scope]: currentContext } :
            {};
    merged[nextContext.scope] = nextContext;
    return merged;
}

/**
 * Handles backend Recalls warning responses by asking for confirmation.
 * @param {object} params Warning params.
 * @returns {Promise<void>}
 */
export async function handleRecallWarningResponse({
    action,
    contactConfirmLabel = "Save anyway",
    confirmedOverrides = {},
    data,
    duplicateConfirmLabel = "Save anyway",
    onSuccess,
    response,
}) {
    if (response?.success !== false) {
        onSuccess?.(response);
        return;
    }

    if (response.type === "contact_warning") {
        const missingFields = response.missingContactFields || [];
        const hasInvalidContact = getInvalidContactFields(missingFields).length > 0;
        if (response.blocked === true) {
            showErrorModal(
                hasInvalidContact ?
                    "Invalid Contact Details" :
                    "Contact Details Required",
                hasInvalidContact ?
                    `${getMissingContactSentence(missingFields)} Fix or clear invalid contact details before saving.` :
                    `${getMissingContactSentence(missingFields)} Add at least one valid email or phone number before saving.`,
            );
            return;
        }
        runAfterConfirmation({
            title: hasInvalidContact ?
                "Invalid Contact Details" :
                "Missing Contact Details",
            body: renderWarningBody([
                getMissingContactSentence(missingFields),
                hasInvalidContact ?
                    getInvalidContactClearSentence(missingFields) :
                    "Do you want to save anyway?",
            ]),
            buttonText: contactConfirmLabel,
            onConfirm: async () => {
                const nextOverrides = {
                    ...confirmedOverrides,
                    contactOverride: {
                        confirmed: true,
                        noEmail: !data.email,
                    },
                };
                const nextResponse = await action(nextOverrides);
                continueWarningResponseAfterConfirmation({
                    action,
                    contactConfirmLabel,
                    confirmedOverrides: nextOverrides,
                    data,
                    duplicateConfirmLabel,
                    onSuccess,
                    response: nextResponse,
                });
            },
        });
        return;
    }

    if (response.type === "duplicate_warning") {
        if (response.blocked === true) {
            openConfirmationModal(
                "Duplicate Recall Blocked",
                renderDuplicateWarningBody(response),
                async () => {},
                false,
                "OK",
                null,
                "OK",
                "Cancel",
                { maxWidth: "sm", scrollBody: true },
            );
            return;
        }
        const hasActive = (response.duplicates?.active || []).length > 0;
        const hasRecentlyClosed =
            (response.duplicates?.recentlyClosed || []).length > 0;
        runAfterConfirmation({
            title: hasRecentlyClosed && !hasActive ?
                "Recently Closed Recall" :
                "Possible Duplicate Recall",
            body: renderDuplicateWarningBody(response),
            buttonText: duplicateConfirmLabel,
            options: { maxWidth: "sm", scrollBody: true },
            onConfirm: async () => {
                const duplicateContext = mergeDuplicateOverrideContext(
                    confirmedOverrides.duplicateContext,
                    response.duplicateContext,
                );
                const nextOverrides = {
                    ...confirmedOverrides,
                    duplicateContext,
                    duplicateOverride: true,
                };
                const nextResponse = await action(nextOverrides);
                continueWarningResponseAfterConfirmation({
                    action,
                    contactConfirmLabel,
                    confirmedOverrides: nextOverrides,
                    data,
                    duplicateConfirmLabel,
                    onSuccess,
                    response: nextResponse,
                });
            },
        });
        return;
    }

    showErrorModal("Recalls Warning", response.message || "The recall was not saved.");
}

async function createPermanentRecallWithWarnings(data, onSuccess, overrides = {}) {
    const action = async (extra = {}) => createRecallEntry({
        data,
        ...overrides,
        ...extra,
    });
    const response = await action();
    await handleRecallWarningResponse({ action, data, onSuccess, response });
}

async function promoteDraftWithWarnings(draft, onSuccess, overrides = {}) {
    const action = async (extra = {}) => promoteRecallDraft({
        draftId: draft.id,
        expectedUpdatedAt: getLatestDraftExpectedUpdatedAt(draft),
        ...overrides,
        ...extra,
    });
    const response = await action();
    await handleRecallWarningResponse({ action, data: draft, onSuccess, response });
}

async function saveDraftData(data, draft = null, expectedUpdatedAt = undefined) {
    return saveRecallDraft({
        data,
        draftId: draft?.id || null,
        expectedUpdatedAt: expectedUpdatedAt !== undefined ?
            expectedUpdatedAt :
            draft?.updatedAt || null,
    });
}

function getEntryMenuBoundary(root) {
    return root.closest("[data-recalls-new-entry-row]") ?
        null :
        "[data-recalls-entry-section]";
}

function mountReasonControls(container) {
    container.querySelectorAll("[data-recalls-entry-reason-control]").forEach((root) => {
        if (root.dataset.controlId) {return;}
        const cell = root.closest("td");
        const mirrorInput = cell?.querySelector("[data-recalls-entry-reason-mirror]");
        if (!mirrorInput) {return;}

        createSelectControl({
            root,
            mirrorInput,
            name: "recall_reason",
            value: normalizeRecallReasonIds(mirrorInput.value),
            options: getReasonControlOptions(),
            placeholder: "+ Add reason",
            multiple: true,
            clearable: true,
            portalMenu: true,
            menuBoundary: getEntryMenuBoundary(root),
        });
    });
}

function mountProviderControls(container) {
    container.querySelectorAll("[data-recalls-entry-provider-control]").forEach((root) => {
        if (root.dataset.controlId) {return;}
        const cell = root.closest("td");
        const mirrorInput = cell?.querySelector("[data-recalls-entry-provider-mirror]");
        if (!mirrorInput) {return;}

        createRecallProviderSelectPreset({
            root,
            appData,
            mirrorInput,
            name: "provider",
            value: parseRecallProviderMirrorValue(mirrorInput.value),
            searchable: true,
            portalMenu: true,
            menuBoundary: getEntryMenuBoundary(root),
        });
    });
}

function mountDateControls(container) {
    container.querySelectorAll("[data-recalls-entry-date-control]").forEach((root) => {
        if (root.dataset.controlId) {return;}
        const cell = root.closest("td");
        const mirrorInput = cell?.querySelector("[data-recalls-entry-date-mirror]");
        if (!mirrorInput) {return;}

        createDateControl({
            root,
            mirrorInput,
            name: "recall_date",
            type: "date",
            value: mirrorInput.value || "",
        });
    });
}

function mountEntryRowControls(container) {
    bindNativeFieldBehavior(container);
    bindRecallPhoneInputSanitizer(container);
    mountReasonControls(container);
    mountProviderControls(container);
    mountDateControls(container);
}

function markEntryBusy(root, busy) {
    root.querySelectorAll("button, input, select, textarea")
        .forEach((control) => {
            control.disabled = busy;
            control.classList.toggle("opacity-70", busy);
        });
}

function getDraftSaveErrorMessage(error) {
    if (getCallableErrorType(error) === "stale_save") {
        return "This draft was changed by someone else. Refresh and try again.";
    }
    return getCallableErrorMessage(error);
}

function showDraftSaveErrorModal(error) {
    showErrorModal("Save Draft", getDraftSaveErrorMessage(error));
    if (error && typeof error === "object") {
        DRAFT_SAVE_ERRORS_WITH_MODAL.add(error);
    }
}

function hasShownDraftSaveErrorModal(error) {
    return Boolean(
        error &&
        typeof error === "object" &&
        DRAFT_SAVE_ERRORS_WITH_MODAL.has(error),
    );
}

async function saveDraftRowNow(draftRow, draft) {
    const draftId = draft?.id;
    if (!draftId) {return false;}
    const session = getDraftSaveSession(draft);
    if (!session) {return false;}
    if (session.inFlight) {
        session.pending = true;
        return session.inFlight.then(() => {
            if (session.pending || hasDraftDataChanges(
                session.baselineData,
                collectRecallEntryData(draftRow),
            )) {
                return saveDraftRowNow(draftRow, draft);
            }
            return false;
        });
    }

    const data = collectRecallEntryData(draftRow);
    if (!hasDraftDataChanges(session.baselineData, data)) {
        return false;
    }

    session.pending = false;
    session.inFlight = (async () => {
        try {
            const response = await saveDraftData(
                data,
                draft,
                session.expectedUpdatedAt,
            );
            session.baselineData = copyDraftEntryData(data);
            session.expectedUpdatedAt = response?.updatedAt || null;
            state.hasUnsavedChanges = false;
            return true;
        } catch (error) {
            state.hasUnsavedChanges = true;
            showDraftSaveErrorModal(error);
            throw error;
        } finally {
            session.inFlight = null;
        }
    })();
    return session.inFlight;
}

async function flushDraftBlurSave(draftRow, draft) {
    return saveDraftRowNow(draftRow, draft);
}

function isDraftRowFocusLeaving(draftRow, event) {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && draftRow.contains(nextTarget)) {
        return false;
    }
    if (nextTarget instanceof Element && nextTarget.closest("[role='listbox']")) {
        return false;
    }
    return true;
}

function renderDraftRow(draft) {
    return `
        <tbody class="recall-draft-row-group border-t border-gray-100" data-recall-draft-row data-draft-id="${attrValue(draft.id)}">
            <tr class="bg-white">
                ${renderEntryInputs(draft)}
                <td class="${ENTRY_ACTION_CELL_CLASS}">
                    <div class="${ENTRY_ACTION_ROW_CLASS}">
                        <button
                            type="button"
                            class="standard-action-icon-btn"
                            data-recalls-draft-action="promote"
                            title="Promote draft to recall"
                            aria-label="Promote draft to recall"
                        >
                            <span class="material-symbols-outlined pointer-events-none text-[18px]">save</span>
                        </button>
                        <button
                            type="button"
                            class="standard-action-icon-btn standard-action-icon-btn--danger"
                            data-recalls-draft-action="delete"
                            title="Delete draft"
                            aria-label="Delete draft"
                        >
                            <span class="material-symbols-outlined pointer-events-none text-[18px]">delete</span>
                        </button>
                    </div>
                </td>
            </tr>
        </tbody>
    `;
}

/**
 * Renders the permanent entry row and shared draft rows.
 * @returns {string} Entry section HTML.
 */
export function renderRecallsEntrySection() {
    return `
        <section class="rounded-lg border border-gray-200 bg-white shadow-sm" data-recalls-entry-section>
            <div class="flex flex-col gap-1 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <h3 class="text-base font-semibold text-gray-900">Draft recalls</h3>
                <span class="text-xs text-gray-500">${appData.recallDrafts?.length || 0} saved drafts</span>
            </div>
            <div class="overflow-x-auto">
                <table class="w-full min-w-[72rem] table-fixed text-left text-sm">
                    <colgroup>
                        ${DRAFT_TABLE_COLUMN_WIDTHS.map((width) => `
                            <col style="width: ${width};">
                        `).join("")}
                    </colgroup>
                    <thead class="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                            <th class="px-2 py-2">First</th>
                            <th class="px-2 py-2">Last</th>
                            <th class="px-2 py-2">Email</th>
                            <th class="px-2 py-2">Phone</th>
                            <th class="px-2 py-2">Reason</th>
                            <th class="px-2 py-2">Provider</th>
                            <th class="px-2 py-2">Recall date</th>
                            <th class="px-2 py-2">Notes</th>
                            <th class="px-1.5 py-2"><span class="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody data-recalls-new-entry-row>
                        <tr>
                            ${renderEntryInputs({})}
                            <td class="${ENTRY_ACTION_CELL_CLASS}">
                                <div class="${ENTRY_ACTION_ROW_CLASS}">
                                    <button
                                        type="button"
                                        class="standard-action-icon-btn"
                                        data-recalls-entry-action="save"
                                        title="Save recall"
                                        aria-label="Save recall"
                                    >
                                        <span class="material-symbols-outlined pointer-events-none text-[18px]">save</span>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    ${(appData.recallDrafts || []).map(renderDraftRow).join("")}
                </table>
            </div>
        </section>
    `;
}

/**
 * Binds Recalls entry and draft row actions.
 * @param {HTMLElement} container Recalls route container.
 * @param {Function} rerender Route rerender callback.
 * @returns {void}
 */
export function bindRecallsEntryActions(container, rerender) {
    mountEntryRowControls(container);

    const newEntryRow = container.querySelector("[data-recalls-new-entry-row]");
    newEntryRow?.addEventListener("input", () => {
        state.hasUnsavedChanges = true;
    });
    newEntryRow?.addEventListener("change", () => {
        state.hasUnsavedChanges = true;
    });

    newEntryRow?.querySelector("[data-recalls-entry-action=\"save\"]")
        ?.addEventListener("click", async () => {
            const data = collectRecallEntryData(newEntryRow);
            if (!hasAnyEntryData(data)) {return;}
            markEntryBusy(newEntryRow, true);
            try {
                if (!hasRequiredPermanentFields(data)) {
                    await saveDraftData(data);
                    state.hasUnsavedChanges = false;
                    rerender();
                    return;
                }

                await confirmNearDateIfNeeded(data, async () => {
                    await createPermanentRecallWithWarnings(data, () => {
                        state.hasUnsavedChanges = false;
                        void refreshRecallsAfterPermanentSave({
                            onData: rerender,
                            recall: data,
                        });
                    });
                });
            } catch (error) {
                if (openRecallsConfigIncompleteModal(error)) {return;}
                showErrorModal(
                    getCallableErrorType(error) === "stale_save" ?
                        "Stale Draft" : "Save Recall",
                    getCallableErrorMessage(error),
                );
            } finally {
                markEntryBusy(newEntryRow, false);
            }
        });

    container.querySelectorAll("[data-recall-draft-row]").forEach((draftRow) => {
        const draft = (appData.recallDrafts || [])
            .find((item) => item.id === draftRow.dataset.draftId);
        if (!draft) {return;}
        getDraftSaveSession(draft);

        draftRow.addEventListener("input", () => {
            state.hasUnsavedChanges = true;
        });
        draftRow.addEventListener("change", () => {
            state.hasUnsavedChanges = true;
        });
        draftRow.addEventListener("focusout", async (event) => {
            if (
                event.target?.type === "hidden" ||
                !isDraftRowFocusLeaving(draftRow, event)
            ) {
                return;
            }
            try {
                await flushDraftBlurSave(draftRow, draft);
            } catch {
                // Failure modal is opened by saveDraftRowNow.
            }
        });

        draftRow.querySelector("[data-recalls-draft-action=\"promote\"]")
            ?.addEventListener("pointerdown", async () => {
                if (
                    document.activeElement &&
                    draftRow.contains(document.activeElement)
                ) {
                    document.activeElement.blur();
                }
            });

        draftRow.querySelector("[data-recalls-draft-action=\"promote\"]")
            ?.addEventListener("click", async () => {
                markEntryBusy(draftRow, true);
                try {
                    const data = collectRecallEntryData(draftRow);
                    await flushDraftBlurSave(draftRow, draft);
                    await confirmNearDateIfNeeded(data, async () => {
                        await promoteDraftWithWarnings({
                            ...draft,
                            ...data,
                        }, (result) => {
                            state.hasUnsavedChanges = false;
                            void refreshRecallsAfterDraftPromotion({
                                draft: {
                                    ...draft,
                                    ...data,
                                },
                                onData: rerender,
                                response: result,
                            });
                        }, {
                            expectedUpdatedAt: getLatestDraftExpectedUpdatedAt(draft),
                        });
                    });
                } catch (error) {
                    if (openRecallsConfigIncompleteModal(error)) {return;}
                    if (!hasShownDraftSaveErrorModal(error)) {
                        showErrorModal("Complete Draft", getCallableErrorMessage(error));
                    }
                } finally {
                    markEntryBusy(draftRow, false);
                }
            });

        draftRow.querySelector("[data-recalls-draft-action=\"delete\"]")
            ?.addEventListener("click", () => {
                openConfirmationModal(
                    "Delete Recall Draft",
                    "Delete this shared Recall draft?",
                    async () => {
                        const session = getDraftSaveSession(draft);
                        if (session?.inFlight) {
                            await session.inFlight;
                        }
                        await deleteRecallDraft({
                            draftId: draft.id,
                            expectedUpdatedAt: getLatestDraftExpectedUpdatedAt(draft),
                        });
                        DRAFT_SAVE_SESSIONS.delete(draft.id);
                        state.hasUnsavedChanges = false;
                        rerender();
                    },
                    true,
                    "Delete",
                );
            });
    });
}

/**
 * Saves quick-add data according to modal completeness rules.
 * @param {object} data Entry payload.
 * @param {Function} onSuccess Success callback.
 * @param {{ mode?: "auto" | "draft" | "save" }} [options={}] Save options.
 * @returns {Promise<void>}
 */
export async function saveRecallQuickAdd(data, onSuccess, options = {}) {
    const mode = options.mode || "auto";
    if (!hasAnyEntryData(data)) {
        showErrorModal("Quick Add Recall", "Enter recall details before saving.");
        return;
    }

    if (mode === "draft") {
        await saveDraftData(data);
        onSuccess?.();
        return;
    }

    const shouldCreatePermanent = mode === "save" ?
        isQuickAddSaveReady(data) :
        isQuickAddPermanentReady(data);
    if (shouldCreatePermanent) {
        await confirmNearDateIfNeeded(data, async () => {
            await createPermanentRecallWithWarnings(data, onSuccess);
        });
        return;
    }

    if (mode === "save") {
        showErrorModal(
            "Save Recall",
            "Complete first name, last name, recall reason, provider, recall date, and enter an email or phone before saving.",
        );
        return;
    }

    await saveDraftData(data);
    onSuccess?.();
}
